import { createContext, JSX, useCallback, useContext, useEffect, useState } from "react";
import {
  useGetBranchesQuery,
  useGetReferralsQuery,
  useGetServicesQuery,
  useGetSettingsQuery,
} from "queries/reservation.query";
import { BranchType, ISingleAvailability, ISingleReferral, ISingleService } from "types";
import { BRAND_COLOR } from "config/colors";
import { useSendTrackingMutation } from "queries/tracking.query";
import { TRACKING } from "config/consts";
import {
  ReservationPayloadAction,
  ReservationPayloadType,
  reservationPayloadReducer,
} from "./reservationPayloadReducer";

interface IAppContextProviderProps {
  children: JSX.Element[] | JSX.Element;
}

export type { ReservationPayloadType } from "./reservationPayloadReducer";

type ConfirmationPayloadType = {
  date: string;
  startTime: string;
  therapist: string;
  bankAccountNumber: string;
  price: string;
  isOnline: boolean;
  branch: BranchType | null;
  firstTime: boolean;
  parentAloneOnFirstVisit: boolean;
};

type AppContextType = {
  currency: string;
  customerId: string;
  branches: BranchType[];
  setBranches: (branches: BranchType[]) => void;
  services: ISingleService[];
  referrals: ISingleReferral[];
  reservationPayload: ReservationPayloadType;
  setReservationPayload: (
    payload: ReservationPayloadType | ((prev: ReservationPayloadType) => ReservationPayloadType),
  ) => void;
  dispatchReservationPayload: (action: ReservationPayloadAction) => void;
  activeTimeslot: ISingleAvailability | null;
  setActiveTimeslot: (timeslot: ISingleAvailability) => void;
  price: string;
  confirmationData: ConfirmationPayloadType | null;
  setConfirmationData: (data: ConfirmationPayloadType) => void;
  getBranchById: (id: string) => BranchType | null;
  mainColor: string;
  activeService: ISingleService | null;
  setActiveService: (service: ISingleService) => void;
  setActiveStep: (step: string) => void;
  sentEvents: string[];
  supportEnglish: boolean;
  patientFormValues: Record<string, unknown> | null;
  setPatientFormValues: (values: Record<string, unknown> | null) => void;
  settings: {
    customer: {
      name: string;
      privacyPolicyLink: string;
      termsLink: string;
      bankAccountNumber?: string | null;
    };
    preferences: {
      maxBankTransferHours: number;
      maxPatientCancelHours: number;
      publicReservationExpiryMinutes: number;
      onlineReservationExpiryMinutes: number;
      paymentLinkMode: number;
      branding: {
        mainColor: string;
        triggerColor: string;
      };
      currency: string;
    };
  };
  isOnlineOnly: boolean;
};

const defaultContext = {
  customerId: "",
  branches: [],
  setBranches: () => {},
  services: [],
  referrals: [],
  currency: "zł",
  reservationPayload: {
    serviceId: "",
    startTime: "",
    scheduleId: "",
    visitTypeId: "",
    therapist: "",
    branchIds: [],
    optionIds: [],
    isOnline: false,
  },
  setReservationPayload: () => {},
  dispatchReservationPayload: () => {},
  activeTimeslot: null,
  setActiveTimeslot: () => {},
  price: "",
  confirmationData: null,
  setConfirmationData: () => {},
  getBranchById: () => null,
  mainColor: "",
  activeService: null,
  setActiveService: () => {},
  setActiveStep: () => {},
  sentEvents: [],
  supportEnglish: true,
  settings: null,
  patientFormValues: null,
  setPatientFormValues: () => {},
  isOnlineOnly: false,
};

export const AppContext = createContext<AppContextType>(defaultContext);

export const AppProvider = ({ children }: IAppContextProviderProps) => {
  const queryParams = new URLSearchParams(window.location.search);
  const customerId = queryParams.get("id");
  const isPersonalFlow = window.location.pathname === "/personal" || queryParams.get("personal") === "1";
  const [currency, setCurrency] = useState<string>("zł");
  const [mainColor, setMainColor] = useState(BRAND_COLOR);
  const supportEnglish = queryParams.has("lang") ? queryParams.get("lang") === "en" : !isPersonalFlow;
  const [sentEvents, setSentEvents] = useState<string[]>([]);
  const [activeStep, setActiveStep] = useState<string>(null);
  const [activeTimeslot, setActiveTimeslot] = useState<ISingleAvailability | null>(null);
  const [price, setPrice] = useState<string>("0");
  const [confirmationData, setConfirmationData] = useState<ConfirmationPayloadType | null>(null);
  const sendTracking = useSendTrackingMutation();
  const [isOnlineOnly, setIsOnlineOnly] = useState(false);
  const [reservationPayload, setReservationPayload] = useState<ReservationPayloadType>({
    serviceId: "",
    startTime: "",
    scheduleId: "",
    visitTypeId: "",
    therapist: "",
    isOnline: false,
    branchIds: [],
    optionIds: [],
  });
  // The reducer owns optionIds-mutating actions so it can enforce the
  // unique-optionIds / single-VISIT_TYPE invariants. setReservationPayload is
  // kept for pass-through setters (birthYear, branchIds, therapist).
  const dispatchReservationPayload = useCallback((action: ReservationPayloadAction) => {
    setReservationPayload((prev) => reservationPayloadReducer(prev, action));
  }, []);
  const [branches, setBranches] = useState<BranchType[]>([]);
  const { data: settings } = useGetSettingsQuery(customerId);
  const { data: allBranches } = useGetBranchesQuery(customerId);
  const { data: services, isLoading: isLoadingServices } = useGetServicesQuery(customerId);
  const { data: referrals, isLoading: isLoadingReferrals } = useGetReferralsQuery(customerId);
  const [activeService, setActiveService] = useState<ISingleService | null>(null);
  const [patientFormValues, setPatientFormValues] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    if (settings) {
      if (settings.preferences && settings.preferences.branding && settings.preferences.branding.mainColor) {
        setMainColor(`#${settings.preferences.branding.mainColor}`);
      }

      if (settings.preferences && settings.preferences.currency) {
        setCurrency(settings.preferences.currency);
      }
    }
  }, [settings]);

  const getVisitPrice = () => {
    let price = 0;
    if (activeTimeslot) {
      const therapistService = activeTimeslot.therapist.therapistServices.find(
        (el) => el.serviceId === reservationPayload.serviceId,
      );

      if (therapistService) {
        price = parseFloat(therapistService.price);

        therapistService.options.forEach((option) => {
          if (reservationPayload.optionIds.includes(option.optionId)) {
            price += parseFloat(option.price);
          }
        });
      }
    }
    return price;
  };

  const getBranchById = (id: string) => {
    return branches.find((el) => el.id === id);
  };

  useEffect(() => {
    if (allBranches || isPersonalFlow) {
      if (!sentEvents.includes(TRACKING.INIT)) {
        setSentEvents([...sentEvents, TRACKING.INIT]);
        window.parent.postMessage({ action: TRACKING.INIT }, "*");
        sendTracking.mutate({ data: { customerId, action: TRACKING.INIT } });
      }

      if (allBranches?.length === 1 && allBranches[0].name === "Online") {
        setIsOnlineOnly(true);
        setReservationPayload({ ...reservationPayload, isOnline: true, branchIds: [allBranches[0].id] });
      }
      if (allBranches) setBranches(allBranches);
    }
  }, [allBranches, isPersonalFlow, sentEvents]);

  useEffect(() => {
    if (activeStep && !sentEvents.includes(activeStep)) {
      setSentEvents([...sentEvents, activeStep]);
      if (activeStep === TRACKING.FINALIZED) {
        window.parent.postMessage({ action: "PURCHASE" }, "*");
      } else {
        window.parent.postMessage({ action: activeStep }, "*");
      }

      sendTracking.mutate({ data: { customerId, action: activeStep } });
    }
  }, [activeStep, sentEvents]);

  useEffect(() => {
    if (reservationPayload.serviceId && activeTimeslot) {
      setPrice(getVisitPrice().toString());
    }
  }, [reservationPayload, activeTimeslot]);

  const value = {
    customerId,
    branches,
    setBranches,
    services,
    referrals,
    reservationPayload,
    setReservationPayload,
    dispatchReservationPayload,
    activeTimeslot,
    setActiveTimeslot,
    price,
    confirmationData,
    setConfirmationData,
    getBranchById,
    mainColor,
    activeService,
    setActiveService,
    setActiveStep,
    sentEvents,
    supportEnglish,
    settings,
    currency,
    patientFormValues,
    setPatientFormValues,
    isOnlineOnly,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  return useContext(AppContext);
};
